// ── Early Bird 双体系分类器 — 产品赛道 + 生态建设 ──────────────────────
// 对 Early Bird 项目（ETHGlobal Showcase / Ethereum Foundation ESP）做两套并行分类：
// - product（产品赛道）：RWA / Stablecoin / AI Agent / Payments / DeFi / Privacy /
//   Identity / DePIN / Gaming-SocialFi / DAO-Governance / Infra-Tooling
// - ecosystem（生态建设）：社区教育 / 安全研究 / 基础设施运维 / 公共物品 / 协议研发，
//   专门覆盖 EF ESP 那批研究与社区资助（非投资标的，UI 需明示）
// 关键词分类器是 v1；medium 置信度与 Others 交给后续 AI 辅助分类二次处理。
// 已知局限：复数/变形不匹配（如 "security assessments" 不命中 "security assessment"），
// 不逐个追复数——这类漏分统一归 AI 辅助分类解决（见探索报告）。

const escapeRegExp = (kw) => kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// 强活动信号：命中即直接判 生态/社区教育，不再进产品分类。
// 依据：一个 summit ABOUT privacy 是活动而不是隐私产品（真实误分案例：
// Web3Privacy Prague Summit 曾被分成 产品/Privacy）。全池副作用已验证：
// 1,829 个项目中仅 1 个 ETHGlobal 边界案例被此规则吸入社区教育。
const EVENT_OVERRIDE_KEYWORDS = [
  'summit', 'conference', 'workshop', 'meetup', 'residency', 'bootcamp',
  'fellowship', 'hackathon sponsorship', 'summer school', 'symposium', 'devcon booth',
];

// 产品赛道：顺序即优先级（窄类目在前，宽类目在后，避免 DeFi 抢走 Stablecoin）
export const PRODUCT_TAXONOMY = [
  ['RWA', ['rwa', 'real-world asset', 'real world asset', 'tokenized treasury', 'tokenized real estate', 'tokenized bond']],
  ['Stablecoin', ['stablecoin', 'stable coin']],
  ['AI Agent', ['ai agent', 'autonomous agent', 'ai agents']],
  ['Payments', ['payment', 'pay anyone', 'payroll', 'checkout', 'escrow', 'ticketing', 'toll payment', 'remittance', 'donation']],
  ['DeFi', ['defi', 'lending', 'yield', 'liquidity pool', 'amm', 'perp', 'dex', 'swap', 'collateral', 'prediction market', 'binary option', 'trading']],
  ['Privacy', ['privacy', 'private', 'stealth address', 'zero-knowledge', 'zk proof', 'zk proofs', 'zk-proof', 'confidential', 'anonym', 'incognito']],
  ['Identity', ['identity', 'credential', 'reputation', 'kyc', 'ens name', 'did:', 'notary', 'digital will']],
  ['DePIN', ['depin', 'physical infrastructure']],
  ['Gaming/SocialFi', ['game', 'gaming', 'social network', 'socialfi', 'roguelike', 'roguelite', 'lottery', 'gambl']],
  ['DAO/Governance', ['\\bdao\\b', 'governance', 'voting']],
  ['Infra/Tooling', ['sdk', 'dev tool', 'indexer', 'oracle', 'rpc node', 'node client', 'wallet', 'proxy', 'sandbox']],
];

// 生态建设：窄类目（安全/运维/公共物品）在前，协议研发的宽词（protocol/research/grant）殿后
export const ECOSYSTEM_TAXONOMY = [
  ['社区/教育/会议', ['workshop', 'meetup', 'conference', 'hackathon', 'community', 'bootcamp', 'curriculum', 'fellowship', 'cohort', 'summer school', 'education', 'training program', 'symposium', 'onboard', 'summit', 'residency']],
  ['安全研究', ['security research', 'audit', 'bug bounty', 'vulnerability', 'red team', 'exploit']],
  ['基础设施运维', ['benchmark', 'performance', 'maintenance', 'bridge grant', 'retainer', 'observability', 'peer-to-peer network', 'rollup', 'security assessment']],
  ['公共物品/治理研究', ['public good', 'policy', 'think tank', 'regulatory', 'rpgf', 'retroactive', 'decentralization research', 'economic model']],
  ['协议研发/密码学研究', ['formal verification', 'circuit', 'consensus mechanism', 'execution layer', 'cryptograph', 'zk-snark', 'lean 4', 'arklib', 'evm', 'client', 'kevm', 'geth', 'peerdas', 'polynomial', 'proof system', 'protocol', 'research', 'internship', 'grant']],
];

// ECOSYSTEM_TAXONOMY 分类名只有中文（`tax_category` 数据库字段本身也只存中文）——
// 英文界面下的展示用映射，不影响筛选用的 key（onClick/查询仍然传中文原值）
export const ECOSYSTEM_CATEGORY_LABELS_EN = {
  '社区/教育/会议': 'Community/Education/Events',
  '安全研究': 'Security Research',
  '基础设施运维': 'Infrastructure Ops',
  '公共物品/治理研究': 'Public Goods/Governance Research',
  '协议研发/密码学研究': 'Protocol/Cryptography R&D',
};

const matchHits = (text, keywords) =>
  keywords.filter((kw) => new RegExp(`\\b${escapeRegExp(kw)}\\b`, 'i').test(text));

const toResult = (system, category, hits) => ({
  system,
  category,
  confidence: hits.length >= 2 ? 'high' : 'medium',
  matchedKeywords: hits,
});

/**
 * 对单个 Early Bird 项目分类。
 * @param {object} project - Early Bird 原始记录（用到 oneLiner / description / technologyTags）
 * @returns {{system: 'product'|'ecosystem'|null, category: string, confidence: 'high'|'medium'|null, matchedKeywords: string[]}}
 *   system 为 null 时 category 固定为 'Others'（关键词无法判断，等待 AI 辅助分类）。
 */
export function classifyEarlyBirdProject(project) {
  const text = `${project?.oneLiner || ''} ${project?.description || ''}`.toLowerCase();
  const tags = (project?.technologyTags || []).map((t) => t.toLowerCase());

  const eventHits = matchHits(text, EVENT_OVERRIDE_KEYWORDS);
  if (eventHits.length > 0) {
    return { system: 'ecosystem', category: '社区/教育/会议', confidence: 'high', matchedKeywords: eventHits };
  }

  if (tags.includes('ai agents')) {
    return { system: 'product', category: 'AI Agent', confidence: 'high', matchedKeywords: ['technologyTags:AI Agents'] };
  }

  for (const [category, keywords] of PRODUCT_TAXONOMY) {
    const hits = matchHits(text, keywords);
    if (hits.length > 0) return toResult('product', category, hits);
  }

  for (const [category, keywords] of ECOSYSTEM_TAXONOMY) {
    const hits = matchHits(text, keywords);
    if (hits.length > 0) return toResult('ecosystem', category, hits);
  }

  return { system: null, category: 'Others', confidence: null, matchedKeywords: [] };
}

/**
 * 批量分类 + 分组计数，供导航 chip 渲染（产品赛道行 + 生态建设行 + Others）。
 * @param {object[]} projects
 * @returns {{product: Record<string, number>, ecosystem: Record<string, number>, others: number, total: number}}
 */
export function countByCategory(projects) {
  const product = {};
  const ecosystem = {};
  let others = 0;
  for (const p of projects || []) {
    const r = classifyEarlyBirdProject(p);
    if (r.system === 'product') product[r.category] = (product[r.category] || 0) + 1;
    else if (r.system === 'ecosystem') ecosystem[r.category] = (ecosystem[r.category] || 0) + 1;
    else others++;
  }
  return { product, ecosystem, others, total: (projects || []).length };
}
